import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, ArrowRight, ChevronLeft } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import toast from 'react-hot-toast';

export function LoginPage() {
  const navigate = useNavigate();
  const { sendOtp, verifyOtp } = useAuth();
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSendOtp = async () => {
    if (!phone || phone.length < 10) {
      toast.error('Ingresa un número de teléfono válido');
      return;
    }
    try {
      setLoading(true);
      await sendOtp(phone);
      setStep('otp');
      toast.success('Código enviado por SMS');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al enviar OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!otp || otp.length < 6) {
      toast.error('Ingresa el código de 6 dígitos');
      return;
    }
    try {
      setLoading(true);
      const result = await verifyOtp(phone, otp);
      if (result.is_new || result.onboarding_step !== 'active') {
        navigate('/onboarding');
      } else {
        navigate('/');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Código inválido');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-black flex flex-col">
      {/* Top section */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 pt-12">
        <img src="/images/logo-full-color.png" alt="PartRunner" className="h-12 mb-8" />
        <h1 className="font-heading text-4xl text-brand-white tracking-wider mb-2">
          PORTAL DE CONDUCTORES
        </h1>
        <p className="text-gray-400 text-sm text-center max-w-xs">
          Gestiona tu perfil, vehículos, disponibilidad y encuentra oportunidades de entrega.
        </p>
      </div>

      {/* Bottom card */}
      <div className="bg-white rounded-t-3xl px-6 py-8 space-y-6">
        {step === 'phone' ? (
          <>
            <div>
              <h2 className="font-heading text-2xl tracking-wide mb-1">INICIA SESIÓN</h2>
              <p className="text-sm text-gray-500">Te enviaremos un código por SMS</p>
            </div>
            <div className="relative">
              <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="tel"
                placeholder="55 1234 5678"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl text-lg focus:outline-none focus:ring-2 focus:ring-brand-yellow focus:border-brand-yellow"
                maxLength={10}
              />
            </div>
            <Button fullWidth size="lg" onClick={handleSendOtp} loading={loading}>
              Continuar
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </>
        ) : (
          <>
            <div>
              <button
                onClick={() => setStep('phone')}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-brand-black mb-3"
              >
                <ChevronLeft size={16} />
                Cambiar número
              </button>
              <h2 className="font-heading text-2xl tracking-wide mb-1">CÓDIGO DE VERIFICACIÓN</h2>
              <p className="text-sm text-gray-500">Enviado a +52 {phone}</p>
            </div>
            <Input
              type="text"
              placeholder="000000"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="text-center text-2xl tracking-[0.5em] font-mono"
              maxLength={6}
            />
            <Button fullWidth size="lg" onClick={handleVerify} loading={loading}>
              Verificar
            </Button>
            <button
              onClick={handleSendOtp}
              className="w-full text-center text-sm text-gray-500 hover:text-brand-black"
            >
              Reenviar código
            </button>
          </>
        )}
      </div>
    </div>
  );
}
